import type { Edge, Node } from '@xyflow/react';
import { EUntaggedVlan } from 'src/modules/network-configuration/edges/types';
import NodeData, {
  NodeDataType,
  NodeCategoryModel,
} from 'src/modules/network-configuration/models/node-data';
import type { NodeModel } from 'src/modules/network-configuration/models/node-model';
import {
  getEdgeData,
  generateNodeId,
} from 'src/modules/network-configuration/models/node-utils';

import { scoreInterfaceForDeviceEdgeOrder } from './import-topology-device-edge-preference';
import { getInferredPeersByPort } from './import-topology-inferred-peers';
import {
  buildNode,
  getEdgeId,
  getEdgeKey,
  normalizeName,
  getDeviceSourcePortId,
  finalizeImportedEdgeEndpoints,
} from './import-topology-shared';
import { findReciprocalSwitchPortId } from './import-topology-switch-peer-port';
import type {
  DeviceIpByVlan,
  DeviceIpByNodeAndVlan,
  ImportedPeerCandidate,
} from './import-topology-types';
import type { ImportContent } from './import-topology-utils';
import {
  invertPortIndexes,
  buildDhcpLeaseIpMap,
  isReservedAutoDeployVlan,
} from './import-topology-utils';
import { mergeVlanMembershipFromBridgePorts } from './import-topology-vlan-bridge';
import { mapDeviceType } from './map-device-type';

export type CfgEdgeAppendParams = {
  c: ImportContent;
  swNode: Node<NodeModel>;
  switchNodes: Node<NodeModel>[];
  nodesByNormalized: Map<string, string>;
  deviceNodes: Node<NodeModel>[];
  deviceVlans: Map<string, Set<number>>;
  deviceIpByNodeAndVlan: Map<string, DeviceIpByNodeAndVlan>;
  edges: Edge[];
  edgeKeys: Set<string>;
  switchPairConnected: Set<string>;
  contentBySwitchId: Map<string, ImportContent>;
  deviceGatewayByVlan: Map<number, string>;
  deviceIdsUsingGatewayFallback: Set<string>;
};

export function appendCfgEdgesForSwitchContent({
  c,
  swNode,
  switchNodes,
  nodesByNormalized,
  deviceNodes,
  deviceVlans,
  deviceIpByNodeAndVlan,
  edges,
  edgeKeys,
  switchPairConnected,
  contentBySwitchId,
  deviceGatewayByVlan,
  deviceIdsUsingGatewayFallback,
}: CfgEdgeAppendParams) {
  const switchIds = new Set(switchNodes.map((n) => n.id));

  const swCategory = swNode.data.category as NodeCategoryModel;
  const portIdByIndex = invertPortIndexes(swCategory.portIndexes || {});
  const dhcpIpByPort = buildDhcpLeaseIpMap(c.data.dhcp?.lease ?? []);
  const peerByPort = getInferredPeersByPort({
    config: c.data,
    category: swCategory,
    deviceName: swCategory.name,
  });

  const vlanIdsByPort = new Map<number, number[]>();
  const untaggedByPort = new Map<number, number | null>();
  const gatewayByVlan = new Map<number, string>();
  (c.data.vlan?.vids ?? []).forEach((v) => {
    if (isReservedAutoDeployVlan(v.vid)) return;
    (v.members ?? []).forEach((m) => {
      const list = vlanIdsByPort.get(m.interface) ?? [];
      if (!list.includes(v.vid)) list.push(v.vid);
      vlanIdsByPort.set(m.interface, list);
      if (m.egress === 'untagged') untaggedByPort.set(m.interface, v.vid);
    });
  });

  mergeVlanMembershipFromBridgePorts(
    vlanIdsByPort,
    untaggedByPort,
    c.data.vlan?.bridge?.ports,
    swCategory.portIndexes || {},
  );
  (c.data.vlan?.bridges ?? []).forEach((b) => {
    if (!b?.vid || isReservedAutoDeployVlan(b.vid)) return;
    if (b.gw) gatewayByVlan.set(b.vid, b.gw);
  });
  const legacyVlanList = (c.data.vlan?.bridge as { vlan_list?: unknown })
    ?.vlan_list;
  if (Array.isArray(legacyVlanList)) {
    legacyVlanList.forEach((vlan) => {
      const vid =
        typeof (vlan as { vlan_id?: unknown }).vlan_id === 'number'
          ? (vlan as { vlan_id: number }).vlan_id
          : 0;
      const gw =
        typeof (vlan as { gw?: unknown }).gw === 'string'
          ? (vlan as { gw: string }).gw
          : '';
      if (vid && gw && !isReservedAutoDeployVlan(vid)) {
        gatewayByVlan.set(vid, gw);
      }
    });
  }

  const ifaceByIndex = new Map(
    (c.data.interface?.interfaces ?? []).map((i) => [i.interface, i]),
  );
  const candidateIndices = new Set<number>();
  ifaceByIndex.forEach((_, idx) => candidateIndices.add(idx));
  peerByPort.forEach((_, idx) => candidateIndices.add(idx));

  const getOrCreateDevice = (
    name: string,
    peerHint?: ImportedPeerCandidate,
  ) => {
    const nk = normalizeName(name);
    const existingId = nodesByNormalized.get(nk);
    if (existingId) {
      if (peerHint?.category?.poe) {
        const node = deviceNodes.find((n) => n.id === existingId);
        const cat = node?.data.category as NodeCategoryModel | undefined;
        if (node && cat && !cat.poe) {
          cat.poe = structuredClone(peerHint.category.poe);
        }
      }
      return existingId;
    }
    const type = peerHint?.nodeType ?? mapDeviceType(name);
    const category: NodeCategoryModel = { ...NodeData[type], name };
    if (peerHint?.category?.poe) {
      category.poe = structuredClone(peerHint.category.poe);
    }
    const id = `${type}-${generateNodeId()}`;
    deviceNodes.push(
      buildNode({
        id,
        nodeType: type,
        category,
        position: { x: 120 + deviceNodes.length * 220, y: 420 },
      }),
    );
    nodesByNormalized.set(nk, id);
    deviceVlans.set(id, new Set());
    return id;
  };

  const scoreInterfaceIndexForOrder = (interfaceIndex: number): number => {
    const pid = portIdByIndex.get(interfaceIndex);
    if (!pid) return -1e9;
    const vids = (vlanIdsByPort.get(interfaceIndex) ?? []).filter(
      (vid) => !isReservedAutoDeployVlan(vid),
    );
    const iface = ifaceByIndex.get(interfaceIndex);
    const inferred =
      peerByPort.get(interfaceIndex)?.matchName ||
      (iface?.name || '').trim() ||
      (vids.length ? `${swCategory.name} ${pid}` : '');
    if (!inferred) return -1e9;
    const ut = untaggedByPort.get(interfaceIndex);
    return scoreInterfaceForDeviceEdgeOrder({
      portId: pid,
      vlanIds: vids,
      intfNameTrimmed: (iface?.name || '').trim(),
      inferredPeerName: inferred,
      untaggedVid: typeof ut === 'number' ? ut : undefined,
    });
  };

  [...candidateIndices]
    .sort((a, b) => {
      const d = scoreInterfaceIndexForOrder(b) - scoreInterfaceIndexForOrder(a);
      if (d !== 0) return d;
      return a - b;
    })
    .forEach((interfaceIndex) => {
      const portId = portIdByIndex.get(interfaceIndex);
      if (!portId) return;

      const intf = ifaceByIndex.get(interfaceIndex);
      const vlanIds = (vlanIdsByPort.get(interfaceIndex) ?? []).filter(
        (vid) => !isReservedAutoDeployVlan(vid),
      );

      const inferredPeerName =
        peerByPort.get(interfaceIndex)?.matchName ||
        (intf?.name || '').trim() ||
        (vlanIds.length ? `${swCategory.name} ${portId}` : '');
      if (!inferredPeerName) return;

      const resolvedPeerId = nodesByNormalized.get(
        normalizeName(inferredPeerName),
      );
      const isSwitchLink =
        !!resolvedPeerId &&
        resolvedPeerId !== swNode.id &&
        switchIds.has(resolvedPeerId);

      if (isSwitchLink) {
        const pairKey = [swNode.id, resolvedPeerId].sort().join('::');
        if (switchPairConnected.has(pairKey)) return;
      }

      const peerHint = peerByPort.get(interfaceIndex);
      const peerId = isSwitchLink
        ? resolvedPeerId!
        : getOrCreateDevice(inferredPeerName, peerHint);

      if (!isSwitchLink) {
        const ipByVlan =
          deviceIpByNodeAndVlan.get(peerId) ??
          new Map<number, DeviceIpByVlan>();
        const leaseIps = dhcpIpByPort.get(interfaceIndex);
        const inferredVlanIds = leaseIps
          ? [...leaseIps.keys()].filter((vid) => !isReservedAutoDeployVlan(vid))
          : [];
        const effectiveVlanIds = vlanIds.length ? vlanIds : inferredVlanIds;

        const set = deviceVlans.get(peerId) ?? new Set<number>();
        effectiveVlanIds.forEach((vid) => {
          if (vid !== 0) set.add(vid);
        });
        deviceVlans.set(peerId, set);

        if (leaseIps) {
          effectiveVlanIds.forEach((vid) => {
            const ip = leaseIps.get(vid);
            if (ip) {
              ipByVlan.set(vid, {
                ip,
                subnet: '',
                subnetMask: '',
                dhcp: true,
              });
            }
          });
          const ip0 = leaseIps.get(0) || leaseIps.get(1);
          if (ip0 && !ipByVlan.get(0)) {
            ipByVlan.set(0, {
              ip: ip0,
              subnet: '',
              subnetMask: '',
              dhcp: true,
            });
          }
        }

        // Static router interfaces are not exported into dhcp.lease (when dhcp=false).
        // Recover them from per-VLAN gateways available in exported cfg.
        const isRouter =
          peerHint?.nodeType === NodeDataType.Router ||
          deviceNodes.find((n) => n.id === peerId)?.type ===
            NodeDataType.Router;
        if (isRouter) {
          const canUseGlobalGatewayFallback =
            deviceIdsUsingGatewayFallback.has(peerId) ||
            deviceIdsUsingGatewayFallback.size === 0;
          let usedGlobalGatewayFallback = false;
          effectiveVlanIds.forEach((vid) => {
            if (ipByVlan.get(vid)?.ip) return;
            const localGatewayIp = gatewayByVlan.get(vid);
            if (localGatewayIp) {
              ipByVlan.set(vid, {
                ip: localGatewayIp,
                subnet: '',
                subnetMask: '',
                dhcp: false,
              });
              return;
            }
            if (!canUseGlobalGatewayFallback) return;
            const gatewayIp = deviceGatewayByVlan.get(vid);
            if (!gatewayIp) return;
            ipByVlan.set(vid, {
              ip: gatewayIp,
              subnet: '',
              subnetMask: '',
              dhcp: false,
            });
            usedGlobalGatewayFallback = true;
          });
          if (usedGlobalGatewayFallback) {
            deviceIdsUsingGatewayFallback.add(peerId);
          }
        }
        deviceIpByNodeAndVlan.set(peerId, ipByVlan);
      }

      const deviceNodeForEdge = !isSwitchLink
        ? deviceNodes.find((n) => n.id === peerId)
        : undefined;

      let peerPortId: string;
      if (isSwitchLink) {
        peerPortId = findReciprocalSwitchPortId(
          swCategory.name,
          resolvedPeerId!,
          switchNodes,
          contentBySwitchId,
          portId,
        );
      } else if (deviceNodeForEdge) {
        peerPortId = getDeviceSourcePortId(
          deviceNodeForEdge.data.category as NodeCategoryModel,
          (deviceNodeForEdge.type as NodeDataType) ||
            NodeDataType.GenericDevice,
        );
      } else {
        peerPortId =
          NodeData[NodeDataType.GenericDevice].ports?.[0]?.id ??
          'generic-device';
      }

      if (!peerPortId) return;

      const edgeKey = getEdgeKey(swNode.id, portId, peerId, peerPortId);
      if (edgeKeys.has(edgeKey)) return;
      edgeKeys.add(edgeKey);

      if (isSwitchLink) {
        const pairKey = [swNode.id, resolvedPeerId].sort().join('::');
        switchPairConnected.add(pairKey);
      }

      const { source, target, sourceHandle, targetHandle } =
        finalizeImportedEdgeEndpoints(swNode.id, portId, peerId, peerPortId);

      const id = getEdgeId(sourceHandle, targetHandle);

      const untagged = untaggedByPort.get(interfaceIndex);
      let untaggedVlan: string;
      if (typeof untagged === 'number') {
        untaggedVlan = `${untagged}`;
      } else if (vlanIds.length > 1) {
        untaggedVlan = EUntaggedVlan.NONE;
      } else {
        untaggedVlan = EUntaggedVlan.AUTO;
      }

      const edgeData = getEdgeData(sourceHandle);

      edges.push({
        ...(edgeData ? { ...edgeData } : {}),
        id,
        type: 'custom',
        source,
        target,
        sourceHandle,
        targetHandle,
        data: {
          vlanIds: vlanIds.length
            ? [...vlanIds].sort((a, b) => a - b)
            : undefined,
          untaggedVlan,
          autoNegotiation: intf?.autoneg ?? true,
          enableMonitoring: intf?.diagnostics ?? false,
          linkDuplex: intf?.duplex ? 'full' : 'half',
          linkMdi: intf?.mdi,
          linkSpeed: intf?.autoneg ? 'auto' : `${intf?.speed ?? 'auto'}`,
          enableLink: intf?.up ?? true,
        },
      });
    });
}
